import { useEffect, useCallback } from 'react';
import { useRoutes, Outlet } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@store';
import { setActivePermissions } from './store/dictionaries/slice';
import {
  useLazyGetTransactionsPermissionsQuery,
  useLazyGetTransactionsWhitelabelQuery,
  useLazyGetTransactionsPaymentTypesQuery,
} from './store/dictionaries/services';
import Transactions from './hoc/transactions/transactions';
import GeneralLedgerRoutes from './routes/general-ledger';
import InternalRoutes from './routes/internal';
import IncomingRoutes from './routes/incoming';
import OutgoingRoutes from './routes/outgoing';
import ExportRoutes from './routes/export';
import NostroRoutes from './routes/nostro';
import ImportRoutes from './routes/import';
import ExchangeRoutes from './routes/exchange';
import CashCollectionRoutes from './routes/cash-collection';
import RestatementTransactions from './hoc/restatement-transactions/list';
import GeneralLedgerTransaction from './hoc/general-ledger/transaction';
import NotificationCenter from './hoc/notification-center/notification-center';
import ExchangeWithoutCorrections from './hoc/exchange-without-corrections';

import ROUTES from './routes';
import ExchangeTransaction from './hoc/exchange/transaction';

export const routes = {
  path: ROUTES.TRANSACTIONS,
  element: <Transactions />,
  children: [
    { ...GeneralLedgerRoutes },
    { ...InternalRoutes },
    { ...IncomingRoutes },
    { ...OutgoingRoutes },
    { ...ExportRoutes },
    { ...NostroRoutes },
    { ...ImportRoutes },
    { ...ExchangeRoutes },
    { ...CashCollectionRoutes },
    {
      path: ROUTES.RESTATEMENT_TRANSACTIONS,
      element: <RestatementTransactions />,
      children: [
        {
          path: ROUTES.RESTATEMENT_TRANSACTION,
          element: <GeneralLedgerTransaction restatement />,
        },
      ],
    },
    {
      path: ROUTES.EXCHANGE_WITHOUT_CORRECTIONS,
      element: <ExchangeWithoutCorrections />,
      children: [
        {
          path: ROUTES.EXCHANGE_WITHOUT_CORRECTIONS_ITEM,
          element: <ExchangeTransaction withoutCorrections />,
        },
      ],
    },
    {
      path: ROUTES.TRANSACTIONS_NOTIFICATIONS_CENTER,
      element: <NotificationCenter />,
    },
  ],
};

const TransactionsRoutes = () => {
  const elements = useRoutes([
    {
      path: '/',
      element: (
        <Outlet
          context={{
            connectionStatus: null,
            sendMessage: null,
          }}
        />
      ),
      children: [{ ...routes }],
    },
  ]);

  const dispatch = useAppDispatch();

  const { activePermissions, whitelabel, paymentTypes } = useAppSelector(
    (state) => state.transactionsDictionaries,
  );

  const [getTransactionsPaymentTypes] =
    useLazyGetTransactionsPaymentTypesQuery();
  const [getTransactionsPermissions] = useLazyGetTransactionsPermissionsQuery();
  const [getTransactionsWhiteLabel] = useLazyGetTransactionsWhitelabelQuery();

  const getTransactionsPermissionsAction = useCallback(() => {
    getTransactionsPermissions();
  }, []);

  const permissions = JSON.parse(
    localStorage.getItem('activePermissions') as string,
  );

  useEffect(() => {
    const companyName = localStorage.getItem('companyName');

    if (!companyName) {
      getTransactionsWhiteLabel();
    }
  }, []);

  useEffect(() => {
    if (!paymentTypes) {
      getTransactionsPaymentTypes();
    }
  }, [paymentTypes]);

  useEffect(() => {
    if (whitelabel) {
      localStorage.setItem('companyName', whitelabel?.data?.value?.companyName);
    }
  }, [whitelabel]);

  useEffect(() => {
    if (permissions && !activePermissions) {
      dispatch(setActivePermissions(permissions));
    } else if (!permissions && !activePermissions) {
      getTransactionsPermissionsAction();
    }
  }, [
    permissions,
    getTransactionsPermissionsAction,
    dispatch,
    activePermissions,
  ]);

  return elements;
};

export default TransactionsRoutes;
